<template>
  <div class="projet-part">
    <h2 v-if="withTitle" id="autresinfos">
      {{ $t("projet.inscription.vue-ensemble.autres-infos") }}
    </h2>

    <RadioWithInputAndValidation
      :model-value="projetAnnuel.contexteProjetEcole"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      :label="$t('projet.inscription.vue-ensemble.projet_ecole.question')"
      :data="trileanValue"
      :option-labels="trileanValueLabels"
      :rules="projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT_LIGHT' ? '' : 'required'"
      :vid="validationGroup + '_4'"
      :vertical-display="false"
      :disabled="readonly"
      @update:model-value="updateContexteProjetEcole"
      @manual-change="$emit('dirtify-project')"
    />
    <GroupCheckboxesWithValidation
      v-model="eddSelectedValues"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      :label="$t('projet.inscription.vue-ensemble.edd.question')"
      :data="eddAllPossibleValues"
      :disabled="readonly"
      :vid="validationGroup + '_5'"
      :vertical-display="true"
      :input-label="$t('projet.inscription.vue-ensemble.edd.autre')"
      :ext-input-value="projetAnnuel.contexteInscritAutre"
      @input-change="eddAutreValueChanged"
      @manual-change="eddCheckboxChanged"
    />

    <RadioWithInputAndValidation
      v-if="projetAnnuel.typeDossier != 'DOSSIER_INSCRIPTION'"
      :model-value="projetAnnuel.maintientProjetAnneeSuivante"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      rules="required_boolean"
      :data="booleanValue"
      :option-labels="booleanValueLabels"
      :vertical-display="true"
      :disabled="readonly"
      :label="$t('projetPage.labellisation.suivi.projet-maintenu')"
      :vid="validationGroup + '_maintientProjetAnneeSuivante'"
      @update:model-value="updateMaintientProjetAnneeSuivante"
      @manual-change="$emit('dirtify-project')"
    />

    <b-message
      v-if="
        !projetAnnuel.maintientProjetAnneeSuivante &&
        projetAnnuel.maintientProjetAnneeSuivante !== null &&
        !readonly
      "
      class="is-warning"
    >
      {{ $t("projetPage.labellisation.suivi.projet-maintenu-warning") }}
    </b-message>

    <br />
    <br />
    <br />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import RadioWithInputAndValidation from "@/views/inputs/RadioWithInputAndValidation.vue"
import GroupCheckboxesWithValidation from "@/views/inputs/GroupCheckBoxesWithValidation.vue"
import { BOOLEAN, TRILEAN } from "@/model/bean/EnumHelper"
import { ProjetAnnuelDTO } from "@/model/bean/GeneratedDTOs"
import { t } from "@/utils/i18n-utils"
import { BMessage } from "buefy"

const EDD_STRINGS = {
  E3D: t("projet.inscription.vue-ensemble.edd.e3d"),
  ECO_ECOLE: t("projet.inscription.vue-ensemble.edd.eco_ecole"),
  AUTRE: t("projet.inscription.vue-ensemble.edd.autre"),
}

export default defineComponent({
  name: "ProjetAutresInfosView",
  components: {
    RadioWithInputAndValidation,
    GroupCheckboxesWithValidation,
    BMessage,
  },
  props: {
    projetAnnuel: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    withTitle: {
      type: Boolean,
      default: true,
    },
    validationActivated: {
      type: Boolean,
      default: false,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
    validationGroup: {
      type: String,
      default: "autresinfos",
    },
  },

  emits: ["dirtify-project", "trigger-validation", "projet-annuel-updated"],
  data() {
    return {
      trileanValue: Object.values(TRILEAN),
      eddAllPossibleValues: Object.values(EDD_STRINGS).filter(
        (value) => value !== EDD_STRINGS.AUTRE,
      ),
      eddSelectedValues: [] as string[],
      booleanValue: Object.values(BOOLEAN),
    }
  },
  computed: {
    trileanValueLabels(): string[] {
      return this.trileanValue.map((value) => {
        return this.$t(`projet.inscription.vue-ensemble.projet_ecole.${value}`).toString()
      })
    },

    booleanValueLabels(): string[] {
      return Object.keys(BOOLEAN).map((key) => {
        return this.$t(`projetPage.labellisation.suivi.${key.toLowerCase()}`).toString()
      })
    },
  },
  watch: {
    projetAnnuel(): void {
      this.projetAnnuelChanged()
    },
  },
  created() {
    this.projetAnnuelChanged()
  },
  methods: {
    projetAnnuelChanged(): void {
      // Refresh eddSelectedValues according to project state
      this.eddSelectedValues = []
      if (this.projetAnnuel.contexteInscritE3d) {
        this.eddSelectedValues.push(EDD_STRINGS.E3D)
      }
      if (this.projetAnnuel.contexteInscritEcoecole) {
        this.eddSelectedValues.push(EDD_STRINGS.ECO_ECOLE)
      }
      if (this.projetAnnuel.contexteInscritAutre) {
        this.eddSelectedValues.push(EDD_STRINGS.AUTRE)
      }
    },

    eddCheckboxChanged(): void {
      this.updateAnnualProject({
        ...this.projetAnnuel,
        contexteInscritE3d: this.eddSelectedValues.includes(EDD_STRINGS.E3D),
        contexteInscritEcoecole: this.eddSelectedValues.includes(EDD_STRINGS.ECO_ECOLE),
        contexteInscritAutre: this.eddSelectedValues.includes(EDD_STRINGS.AUTRE)
          ? this.projetAnnuel.contexteInscritAutre || ""
          : "",
      })
    },

    eddAutreValueChanged(value: string): void {
      this.updateAnnualProject({
        ...this.projetAnnuel,
        contexteInscritAutre: value,
      })
    },

    updateContexteProjetEcole(newValue: string): void {
      this.updateAnnualProject({
        ...this.projetAnnuel,
        contexteProjetEcole: newValue,
      })
    },

    updateMaintientProjetAnneeSuivante(newValue: boolean): void {
      this.updateAnnualProject({
        ...this.projetAnnuel,
        maintientProjetAnneeSuivante: newValue,
      })
    },

    updateAnnualProject(updatedProjetAnnuel: ProjetAnnuelDTO): void {
      this.modified()
      this.$emit("projet-annuel-updated", updatedProjetAnnuel)
    },

    modified(): void {
      this.$nextTick(() => {
        this.$emit("trigger-validation")
        this.$emit("dirtify-project")
      })
    },
  },
})
</script>
